/**
 * @fileoverview Templates da API oficial (Meta) no editor de triagem, usados
 * pelo Contato ativo e pelos Lembretes: a lista da conexão, se o template serve
 * para a triagem, a origem de cada variável ({{1}}, {{2}}...) e a prévia.
 */

import React, { useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import api from "../../../../services/api";
import { i18n } from "../../../../translate/i18n";
import { extractTemplateVars } from "../../../../utils/templateVars";
import { missingVariable, previewText, templateBodyText, templateProblemKey } from "../../../../utils/metaTemplateRules";
import ModernSelect from "../../../../components/ModernSelect";
import useStyles from "./styles";

const o = (key, params) => i18n.t(`triage.manager.editor.outreach.${key}`, params);

// Ordem do menu de cada variável; "fixed" guarda o texto como "fixed:<texto>".
// As "workOrder.*" vêm da OS que abriu a triagem (aba Ordens de serviço).
const SOURCES = [
  "contact.firstName",
  "contact.name",
  "company.name",
  "workOrder.customerFirstName",
  "workOrder.customerName",
  "workOrder.number",
  "workOrder.productBrand",
  "workOrder.productModel",
  "fixed",
];
// Chave da tradução de cada origem (sem ponto: o i18next leria como aninhamento).
const ROTULO_ORIGEM = {
  "contact.firstName": "contactFirstName",
  "contact.name": "contactName",
  "company.name": "companyName",
  "workOrder.customerFirstName": "woCustomerFirstName",
  "workOrder.customerName": "woCustomerName",
  "workOrder.number": "woNumber",
  "workOrder.productBrand": "woProductBrand",
  "workOrder.productModel": "woProductModel",
  fixed: "fixed",
};
const EXEMPLO = {
  "contact.firstName": "Maria",
  "contact.name": "Maria Souza",
  "company.name": "Assistência",
  "workOrder.customerFirstName": "Maria",
  "workOrder.customerName": "Maria Souza",
  "workOrder.number": "BRWO000123456",
  "workOrder.productBrand": "Midea",
  "workOrder.productModel": "42AGVCI12M5",
};

// O corpo, a regra de "pode sair sozinho" e a variável sem valor ficam em
// utils/metaTemplateRules (uma cópia só, também usada pelas abordagens
// inteligentes); os nomes antigos continuam valendo aqui.
export const corpoDe = templateBodyText;
export { missingVariable, templateProblemKey };

/** Por que o template não serve para a triagem (null: serve), no texto da triagem. */
export const templateProblem = (tpl) => {
  const key = templateProblemKey(tpl);
  return key ? o(key) : null;
};

/** Templates aprovados da conexão (só carrega com `enabled`). */
export const useMetaTemplates = (connectionId, enabled) => {
  const [templates, setTemplates] = useState({ loading: false, error: false, list: [] });
  useEffect(() => {
    if (!enabled || !connectionId) return undefined;
    let alive = true;
    setTemplates({ loading: true, error: false, list: [] });
    api
      .get(`/meta/templates/${connectionId}`)
      .then(({ data }) => {
        if (alive) setTemplates({ loading: false, error: false, list: data?.templates || [] });
      })
      .catch(() => {
        if (alive) setTemplates({ loading: false, error: true, list: [] });
      });
    return () => {
      alive = false;
    };
  }, [connectionId, enabled]);
  return templates;
};

/** O template salvo, na lista da conexão (null: não está mais lá). */
export const findTemplate = (list, value) =>
  value ? list.find((t) => t.name === value.name && t.language === value.language) || null : null;

/**
 * Template escolhido na lista → configuração salva. Mesmo template: mantém os
 * valores das variáveis; novo: {{1}} já vem com o primeiro nome do contato.
 */
export const templateChoice = (tpl, anterior) => {
  const mesmo = anterior && anterior.name === tpl.name && anterior.language === tpl.language;
  const antes = mesmo ? anterior.variables || {} : {};
  const variables = {};
  extractTemplateVars(corpoDe(tpl)).forEach((n) => {
    variables[n] = antes[n] ?? (n === "1" ? "contact.firstName" : "");
  });
  return { name: tpl.name, language: tpl.language, variables };
};

const origemDoMenu = (origem) => (origem && origem.startsWith("fixed:") ? "fixed" : origem || "");

/** Origens da triagem: valor, rótulo e o exemplo da prévia. "fixed" abre o campo de texto. */
const triageSources = () =>
  SOURCES.map((s) => ({ value: s, label: o(`sources.${ROTULO_ORIGEM[s]}`), example: EXEMPLO[s] }));

/**
 * Origem de cada variável do template escolhido e a prévia com dados de
 * exemplo. O template pode ter mudado depois de salvo (variável nova ou a
 * menos): as variáveis passam a ser as do texto atual; a nova fica sem valor,
 * e salvar pede para escolher.
 * `sources` (opcional): outra lista de origens `{ value, label, example }`,
 * usada pelas abordagens inteligentes; sem ela, as da triagem. `showPreview`
 * (padrão sim): a prévia do texto embaixo das variáveis.
 */
export const TemplateFields = ({ value, chosen, onChange, sources, showPreview = true }) => {
  const classes = useStyles();
  const options = sources || triageSources();
  const variaveis = extractTemplateVars(corpoDe(chosen));

  const chavesSalvas = Object.keys(value?.variables || {}).sort().join(",");
  const chavesDoTexto = [...variaveis].sort().join(",");
  useEffect(() => {
    if (!chosen || !value || chavesSalvas === chavesDoTexto) return;
    const antes = value.variables || {};
    const variables = {};
    variaveis.forEach((n) => {
      variables[n] = antes[n] ?? "";
    });
    onChange({ ...value, variables });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, chavesSalvas, chavesDoTexto]);

  if (!chosen || !value) return null;

  const setVariavel = (n, origem) => onChange({ ...value, variables: { ...value.variables, [n]: origem } });

  const previa = previewText(chosen, value.variables, options);

  return (
    <>
      {variaveis.length > 0 && (
        <div className={classes.fieldsBox}>
          <div>
            <div className={classes.label}>{o("variables")}</div>
            <div className={classes.hint}>{o("variablesHint")}</div>
          </div>
          {variaveis.map((n) => {
            const origem = value.variables?.[n] || "";
            const menu = origemDoMenu(origem);
            return (
              <div key={n} className={classes.varRow}>
                <span className={`${classes.chip} ${classes.chipOutline}`}>{`{{${n}}}`}</span>
                <ModernSelect
                  size="small"
                  className={classes.field}
                  value={menu}
                  displayEmpty
                  inputProps={{ "aria-label": o("variableLabel", { n }) }}
                  onChange={(ev) => setVariavel(n, ev.target.value === "fixed" ? "fixed:" : ev.target.value)}
                >
                  <MenuItem value="">
                    <em>{o("sourceNone")}</em>
                  </MenuItem>
                  {options.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </ModernSelect>
                {menu === "fixed" ? (
                  <TextField
                    variant="outlined"
                    size="small"
                    className={classes.field}
                    placeholder={o("fixedPlaceholder")}
                    value={origem.slice(6)}
                    inputProps={{ maxLength: 200, "aria-label": o("fixedLabel", { n }) }}
                    onChange={(ev) => setVariavel(n, `fixed:${ev.target.value.replace(/[\n\r\t]/g, " ")}`)}
                  />
                ) : (
                  <span />
                )}
              </div>
            );
          })}
        </div>
      )}
      {showPreview && (
        <div>
          <div className={classes.label}>{o("preview")}</div>
          <div className={classes.tplPreview}>{previa}</div>
        </div>
      )}
    </>
  );
};

